import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

export default function ResetPassword() {
  const [params]            = useSearchParams();
  const navigate            = useNavigate();
  const token               = params.get('token') || '';
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/admin'), 3000);
    } catch (err) {
      setError(err.message || 'Reset failed. Link may have expired.');
    } finally {
      setLoading(false);
    }
  }

  const inputBase = (extra = {}) => ({
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12, padding: '10px 44px 10px 14px',
    fontSize: 14, color: '#fff', width: '100%', outline: 'none',
    transition: 'border-color 160ms ease', ...extra,
  });

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#08070f' }}>
      <div className="text-center">
        <p className="text-white/60 mb-4">Invalid reset link.</p>
        <Link to="/admin/forgot-password" className="text-sm font-semibold" style={{ color: ACCENT }}>Request a new one</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#08070f' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(94,14,215,0.10) 0%, transparent 70%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
        className="w-full max-w-md relative"
      >
        <div className="flex flex-col items-center mb-10">
          <img src="/logo.png" alt="Syenovix" className="w-14 h-14 rounded-2xl object-cover mb-4" style={{ filter: 'drop-shadow(0 0 10px rgba(94,14,215,0.5))' }} />
          <h1 className="text-xl font-bold tracking-widest uppercase text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>New Password</h1>
        </div>

        <div className="rounded-[1.75rem] p-[1px]" style={{ background: 'linear-gradient(135deg, rgba(94,14,215,0.4), rgba(255,255,255,0.06))' }}>
          <div className="rounded-[1.7rem] p-8" style={{ background: '#0f0d1a' }}>
            {done ? (
              <div className="text-center py-4">
                <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: '#22c55e' }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Password updated! Redirecting to login...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Enter your new password below.</p>

                {/* New password */}
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>New Password</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'} required value={password}
                      onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters"
                      style={inputBase()}
                      onFocus={e => e.target.style.borderColor = ACCENT}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.10)'}
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1" style={{ color: 'rgba(255,255,255,0.4)' }} tabIndex={-1}>
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'} required value={confirm}
                      onChange={e => setConfirm(e.target.value)} placeholder="Repeat password"
                      style={inputBase()}
                      onFocus={e => e.target.style.borderColor = ACCENT}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.10)'}
                    />
                    <button type="button" onClick={() => setShowConfirm(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1" style={{ color: 'rgba(255,255,255,0.4)' }} tabIndex={-1}>
                      {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {/* Password strength hint */}
                {password && (
                  <div className="flex gap-1">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-1 flex-1 rounded-full" style={{ background: password.length > i * 3 ? (password.length < 8 ? '#f97316' : '#22c55e') : 'rgba(255,255,255,0.1)' }} />
                    ))}
                  </div>
                )}

                {error && <p className="text-xs text-red-400 bg-red-400/10 px-3 py-2 rounded-lg">{error}</p>}

                <motion.button
                  type="submit"
                  whileTap={{ scale: 0.97 }}
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl text-sm font-bold uppercase text-white"
                  style={{ background: ACCENT, opacity: loading ? 0.7 : 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </motion.button>
              </form>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
